import { AsyncPipe } from "@angular/common";
import { Component, effect, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Contact, ContactCreated, PendingCivilianContact,
	SharedContactRepository, SharedPendingCivilianContactRepository,
	UserStore} from "@shared/src/public-api";
import { map, Observable, startWith } from "rxjs";
import { AddContactComponent } from "../add-contact/add-contact.component";

@Component({
	selector: "jhm-select-contact-search",
	imports: [
		MatFormFieldModule,
		MatInputModule,
		FormsModule,
		MatAutocompleteModule,
		ReactiveFormsModule,
		AsyncPipe,
		AddContactComponent
	],
	templateUrl: "./select-contact-search.component.html",
	styleUrl: "./select-contact-search.component.scss"
})
export class SelectContactSearchComponent implements OnInit {
	@Input() set selectedContactId(value: string) {
		this.passedInSelectedContactId = value;
		if (this.contacts) {
			this.selectContactWithId(this.passedInSelectedContactId);
		}
	}
    @Input() showReleasedInmates: boolean = false;
	@Output()
    public contactChange: EventEmitter<Contact> = new EventEmitter<Contact>();

	public myControl: FormControl = new FormControl("");
	public showAddContactModal: boolean = false;
	public loading: boolean = false;
	public contacts: Contact[] = [];
	private emailRegex: RegExp = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
	public showInvalidEmailError: boolean = false;
	public tempEmailOrBooking: string;
	public selectedContact: Contact;
	public filteredOptions: Observable<Contact[]>;
	public isInmate: boolean = false;
	public passedInSelectedContactId: string;

	readonly userStore = inject(UserStore);

	public constructor(private contactRepository: SharedContactRepository, private civilianRepository: SharedPendingCivilianContactRepository
	) {
		effect(() => {
			this.isInmate = this.userStore.type() === "INMATE";
		})
	}

	public selectContactWithId(contactId: string): void {
		const recipient: Contact = this.contacts.find((contact: Contact) =>
		{
			return contact.contactId === contactId;
		});
		this.setSelectedContact(recipient);
	}

	public ngOnInit(): void
	{
		this.contactRepository.getContactsPage("").subscribe((contactsPage: Contact[]) =>
		{
			this.setupContacts(contactsPage);
		});
	}

	public createContactExit(createdContact: ContactCreated): void
	{
		this.showAddContactModal = false;
		if (createdContact)
		{
			this.loading = true;
			this.contactRepository.getContactsPage("").subscribe((contactsPage: Contact[]) => {
				this.setupContacts(contactsPage);
				const recipient: Contact = this.contacts.find((contact: Contact) =>
				{
					return contact.contactId === createdContact.contactUserId;
				});
				this.setSelectedContact(recipient);
				this.loading = false;
			})
		}
	}

	public addContactWithNewEmailWithContext(email: string)
	{
		const isValidEmail: boolean = !!this.emailRegex.exec(email);
		if (isValidEmail)
		{
			this.showInvalidEmailError = false;
			this.civilianRepository.createPendingCivilianContact(email).subscribe(
				(newPendingContact: PendingCivilianContact) =>
				{
					if (this.contacts.filter((contact: Contact) => contact.contactEmailOrBookingNumber === email).length === 0)
					{
						const newContact: Contact = new Contact();
						newContact.contactId = newPendingContact.id;
						newContact.contactJurisdictionId = newPendingContact.jurisdictionId;
						newContact.contactFirstName = "";
						newContact.contactLastName = "";
						newContact.contactType = "CIVILIAN";
						newContact.contactEmailOrBookingNumber = newPendingContact.email;
						this.contacts = [...this.contacts, newContact];
						this.setSelectedContact(newContact);
					}
				}
			);
		}
		else
		{
			this.showInvalidEmailError = true;
		}
	}

	public setSelectedContact(newSelectedContact: Contact): void
	{
		let contactString: string = "";
		if (newSelectedContact && this.contacts)
		{
			if (newSelectedContact.isNew)
			{
				this.addContactWithNewEmailWithContext(this.tempEmailOrBooking);
				contactString = newSelectedContact.contactEmailOrBookingNumber;
			}
			else
			{
				this.selectedContact = newSelectedContact;
				this.contactChange.emit(this.selectedContact);

				contactString = this.selectedContact.contactFirstName + " " +
						this.selectedContact.contactLastName;
				if (this.selectedContact.contactType === "CIVILIAN")
				{
					if (this.selectedContact.contactFirstName || this.selectedContact.contactLastName)
					{
						contactString = contactString + " (" + this.selectedContact.contactEmailOrBookingNumber + ")";
					}
					else
					{
						contactString = this.selectedContact.contactEmailOrBookingNumber;
					}
				}
			}
		}
		this.myControl.setValue(contactString);
	}

	public clearContact()
	{
		this.selectedContact = null;
	}

	public dropdownClosed(): void {
		if (this.myControl.value === "") {
			this.selectedContact = null;
			this.contactChange.emit(null);
		}
	}

	private setupContacts(contactsPage: Contact[]): void
	{
		this.contacts = contactsPage;
		this.filteredOptions = this.myControl.valueChanges.pipe(
			startWith(""),
			map((value: string) =>
			{
				this.clearContact();
				return this._filter(value || "");
			})
		);
		if (this.passedInSelectedContactId) {
			this.selectContactWithId(this.passedInSelectedContactId);
		}
	}

	private _filter(value: string): Contact[]
	{
		let contacts: Contact[];
		if (this.showReleasedInmates) {
			contacts = this.contacts;
		}
		else {
			contacts = this.contacts.filter((contact: Contact) => {
				return !contact.hasBeenReleased;
			});
		}
		if (value)
		{
			const filterValue: string = value.toLowerCase();
			contacts = this.contacts.filter(((contact: Contact) =>
			{
				return (contact.contactFirstName + " " + contact.contactLastName).toLowerCase().includes(filterValue) ||
					contact.contactEmailOrBookingNumber.toLowerCase().includes(filterValue) ||
					(this.selectedContact && contact.contactId === this.selectedContact.contactId);
			}));

			if (this.selectedContact && contacts.length === 1 && contacts[0].contactId === this.selectedContact.contactId)
			{
				contacts = this.contacts;
			}
			else if (contacts.length < 1)
			{
				if (this.isInmate)
				{
					const newContact: Contact = new Contact();
					newContact.contactEmailOrBookingNumber = value;
					newContact.isNew = true;
					this.tempEmailOrBooking = value;
					contacts = [newContact];
				}
				else
				{
					contacts = [];
				}
			}
		}
		return contacts;
	}
}
